import { Link } from "react-router-dom";
import { Phone, CalendarCheck, Truck, Car, Disc3, Check } from "lucide-react";
import Stars from "../components/Stars";
import Seo from "../components/Seo";
import { useEffect, useState } from "react";
import { fetchTestimonials } from "../lib/api";

// Red horizontal divider line (signature visual from mockup IMG_9602)
const Divider = () => (
  <div className="w-full" aria-hidden="true" style={{ height: 1, background: "linear-gradient(90deg, transparent 0%, rgba(230,0,0,0.5) 20%, var(--primary-red) 50%, rgba(230,0,0,0.5) 80%, transparent 100%)" }} />
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-4 my-2">
    <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
    <h2 className="px-2 whitespace-nowrap">{children}</h2>
    <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
  </div>
);

const MobileFitting = () => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { fetchTestimonials().then(setReviews).catch(() => {}); }, []);

  return (
    <div className="vd-page" data-testid="page-mobile-fitting">
      <Seo
        title="Mobile Tyre Fitting & 24/7 Roadside Assistance"
        description="Professional mobile tyre fitting at your home, work or roadside in Middlesbrough & Teesside. £25 call-out, free fitting with any tyre purchase, 24/7 emergency cover."
        path="/mobile-fitting"
      />
      {/* HERO */}
      <section className="vd-section-secondary pt-12 pb-6">
        <div className="vd-container">
          <SectionTitle>24/7 Mobile Tyre Fitting &amp; Roadside Assistance</SectionTitle>
          <p className="text-center vd-body text-lg mt-4 mb-10">Fast, professional service wherever you are — home, work, or roadside.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start" id="roadside">
              <Link to="/checkout" className="vd-btn-primary" data-testid="mf-book-fitting"><CalendarCheck size={18} /> Book Mobile Fitting</Link>
              <a href="tel:07979210250" className="vd-btn-secondary" data-testid="mf-call-emergency"><Phone size={18} /> Call for Emergency Support</a>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80"
                alt="ValueDrive Auto Solutions mobile fitting van"
                className="rounded-xl w-full object-cover"
                style={{ aspectRatio: "16/9", border: "1px solid var(--border-color)" }}
                data-testid="mf-hero-image"
              />
              {/* Red glow underline */}
              <div className="absolute -bottom-2 left-4 right-4 h-1 rounded-full" style={{ background: "var(--primary-red)", filter: "blur(8px)", opacity: 0.7 }} aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      <div className="vd-container py-2"><Divider /></div>

      {/* MECHANIC + BULLETS */}
      <section className="vd-container py-14 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <img
          src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=900&q=80"
          alt="Mechanic fitting tyre"
          className="rounded-xl w-full object-cover"
          style={{ aspectRatio: "4/3", border: "1px solid var(--border-color)" }}
        />
        <ul className="space-y-5">
          {[
            { Icon: Truck, t: "Fully Equipped Mobile Vans" },
            { Icon: CalendarCheck, t: "24/7 Emergency Callouts" },
            { Icon: Disc3, t: "Tyre Replacement & Puncture Repairs" },
            { Icon: Car, t: "Serving Middlesbrough & Teesside" },
          ].map((row, i) => (
            <li key={i} className="flex items-center gap-4">
              <span className="inline-flex items-center justify-center rounded-full" style={{ width: 12, height: 12, background: "var(--primary-red)" }} aria-hidden="true" />
              <row.Icon size={22} color="var(--primary-red)" />
              <span className="vd-heading text-lg font-medium">{row.t}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="vd-container py-2"><Divider /></div>

      {/* HOW IT WORKS */}
      <section className="py-14">
        <div className="vd-container">
          <SectionTitle>How It Works</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { n: 1, Icon: CalendarCheck, t: "Book Online or Call" },
              { n: 2, Icon: Truck,         t: "We Arrive at Your Location" },
              { n: 3, Icon: Disc3,         t: "Tyre Fitted & Checked" },
              { n: 4, Icon: Car,           t: "Drive Away Safely" },
            ].map((s) => (
              <div key={s.n} className="text-center flex flex-col items-center" data-testid={`how-step-${s.n}`}>
                <div className="relative mb-4">
                  <div className="flex items-center justify-center rounded-2xl" style={{ width: 96, height: 96, background: "rgba(230,0,0,0.08)", border: "1px solid rgba(230,0,0,0.35)" }}>
                    <s.Icon size={42} color="var(--primary-red)" strokeWidth={1.7} />
                  </div>
                  <span
                    className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full font-extrabold text-sm"
                    style={{ width: 28, height: 28, background: "var(--primary-red)", color: "#fff" }}
                  >{s.n}</span>
                </div>
                <p className="vd-heading font-semibold">
                  <span style={{ color: "var(--primary-red)" }}>{s.n}.</span> {s.t}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="vd-container py-2"><Divider /></div>

      {/* PRICING */}
      <section className="py-14">
        <div className="vd-container text-center">
          <SectionTitle>Pricing &amp; Availability</SectionTitle>
          <p className="mt-8" style={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
            <span style={{ color: "var(--primary-red)" }}>£25</span>{" "}
            <span className="vd-muted" style={{ fontSize: 18, fontWeight: 500 }}>Call-Out Fee</span>
          </p>
          <p className="vd-body mt-5 inline-flex items-center gap-2 justify-center">
            <span className="inline-flex items-center justify-center rounded" style={{ width: 22, height: 22, background: "var(--primary-red)" }}><Check size={14} color="#fff" /></span>
            Free Fitting with Tyre Purchase
          </p>
          <div className="mt-6">
            <Link to="/checkout" className="vd-btn-primary" data-testid="pricing-book-now">Book Now</Link>
          </div>
        </div>
      </section>

      <div className="vd-container py-2"><Divider /></div>

      {/* REVIEWS */}
      <section className="py-14 vd-section-secondary">
        <div className="vd-container">
          <SectionTitle>Customer Reviews</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {reviews.slice(0, 3).map((r) => (
              <div key={r.id} className="relative vd-card p-6" data-testid={`mf-review-${r.id}`}>
                <Stars value={r.stars} />
                <p className="vd-body mt-3 mb-4">“{r.quote}”</p>
                <p className="vd-heading font-semibold text-right">— {r.author}</p>
                {/* Speech bubble pointer */}
                <span aria-hidden="true" className="absolute" style={{ left: 32, bottom: -10, width: 18, height: 18, background: "var(--card-bg)", border: "1px solid var(--border-color)", borderTop: "none", borderLeft: "none", transform: "rotate(45deg)" }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="vd-container py-2"><Divider /></div>

      {/* URGENT HELP */}
      <section className="py-14">
        <div className="vd-container text-center">
          <SectionTitle>Need urgent help?</SectionTitle>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <a href="tel:07979210250" className="vd-btn-primary" data-testid="urgent-call-now"><Phone size={18} /> Call Now</a>
            <Link to="/checkout" className="vd-btn-secondary" data-testid="urgent-book-fitting"><CalendarCheck size={18} /> Book Mobile Fitting</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileFitting;
